// src/utils/api.js
// ─────────────────────────────────────────────────────────────
// All Firestore & Storage operations — acts as the API layer.
// Import these functions anywhere in the app instead of calling
// Firestore directly, so switching backends later is easy.
// ─────────────────────────────────────────────────────────────

import {
  collection, doc, addDoc, updateDoc, deleteDoc, setDoc,
  getDoc, getDocs, query, where, orderBy, limit,
  startAfter, serverTimestamp, increment,
} from "firebase/firestore";
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase";

const PROPERTIES = "properties";
const USERS = "users";
const ENQUIRIES = "enquiries";

// ════════════════════════════════════════════════════
// PROPERTIES
// ════════════════════════════════════════════════════

/**
 * Create a new property listing.
 * Status starts as "pending" — admin approves to "approved".
 * Can be changed to "approved" for immediate visibility.
 */
export async function createProperty(data, userId) {
  const docRef = await addDoc(collection(db, PROPERTIES), {
    title: data.title,
    type: data.type,
    listingType: data.listingType,
    price: data.price,
    location: data.location,
    area: data.area,
    contact: data.contact,
    ownerId: userId,
    status: "pending",  // Changed to pending for admin approval
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    // Optional fields
    bedrooms: data.bedrooms || null,
    bathrooms: data.bathrooms || null,
    floor: data.floor || null,
    facing: data.facing || null,
    furnishing: data.furnishing || null,
    description: data.description || "",
    images: data.images || [],
    views: 0,
    enquiries: 0,
    urgent: data.urgent || false,
    ownerOnly: data.ownerOnly || false,
    verified: false,
  });
  
  // Ensure user document exists before updating
  const userRef = doc(db, USERS, userId);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    // User doc exists, increment listing count
    await updateDoc(userRef, { listingsCount: increment(1) });
  } else {
    // User doc doesn't exist, create it with initial listing count
    await setDoc(userRef, {
      uid: userId,
      email: "",
      name: "",
      phone: "",
      photoURL: "",
      role: "user",
      verified: false,
      createdAt: serverTimestamp(),
      listingsCount: 1,
    });
  }
  
  return docRef.id;
}

/**
 * Fetch a single property by ID.
 * Also increments the view counter.
 */
export async function getProperty(id) {
  const snap = await getDoc(doc(db, PROPERTIES, id));
  if (!snap.exists()) return null;
  await updateDoc(doc(db, PROPERTIES, id), { views: increment(1) });
  return { id: snap.id, ...snap.data() };
}

/**
 * Fetch properties with optional filters.
 * All filters are optional — pass undefined to skip.
 *
 * @param {object} filters
 * @param {string} filters.type       — "House" | "Plot" | "Flat" | "Shop" | "Commercial"
 * @param {string} filters.location   — e.g. "Civil Lines"
 * @param {number} filters.minPrice
 * @param {number} filters.maxPrice
 * @param {boolean} filters.urgent
 * @param {string} filters.status     — default "approved"
 * @param {number} filters.pageSize   — default 12
 * @param {object} filters.lastDoc    — for pagination (pass last Firestore doc snapshot)
 */
export async function getProperties(filters = {}) {
  const {
    type, location, minPrice, maxPrice, urgent,
    status = "approved", pageSize = 12, lastDoc,
  } = filters;

  let q = query(
    collection(db, PROPERTIES),
    where("status", "==", status),
    orderBy("createdAt", "desc"),
    limit(pageSize)
  );

  if (type)      q = query(q, where("type", "==", type));
  if (location)  q = query(q, where("location", "==", location));
  if (urgent)    q = query(q, where("urgent", "==", true));
  if (lastDoc)   q = query(q, startAfter(lastDoc));

  const snap = await getDocs(q);
  let results = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Client-side price filter (Firestore needs index for range + orderBy)
  if (minPrice) results = results.filter(p => p.price >= minPrice);
  if (maxPrice) results = results.filter(p => p.price <= maxPrice);

  return {
    properties: results,
    lastDoc: snap.docs[snap.docs.length - 1] || null,
    hasMore: snap.docs.length === pageSize,
  };
}

/**
 * Fetch all listings owned by a specific user.
 */
export async function getUserProperties(userId) {
  const q = query(
    collection(db, PROPERTIES),
    where("ownerId", "==", userId),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Update a property listing.
 */
export async function updateProperty(id, data) {
  await updateDoc(doc(db, PROPERTIES, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a property listing and its images from Storage.
 */
export async function deleteProperty(id, userId) {
  const prop = await getProperty(id);
  if (!prop) return;

  // Delete images from Storage
  if (prop.images?.length) {
    await Promise.all(
      prop.images.map(url => {
        const imgRef = ref(storage, url);
        return deleteObject(imgRef).catch(() => {}); // ignore missing
      })
    );
  }

  await deleteDoc(doc(db, PROPERTIES, id));
  await updateDoc(doc(db, USERS, userId), { listingsCount: increment(-1) });
}

// ════════════════════════════════════════════════════
// IMAGE UPLOAD
// ════════════════════════════════════════════════════

/**
 * Upload a property image to Firebase Storage.
 * Returns a Promise that resolves to the download URL.
 *
 * @param {File} file           — image file
 * @param {string} userId       — uploader's UID
 * @param {function} onProgress — callback(percent: number)
 */
export function uploadPropertyImage(file, userId, onProgress) {
  return new Promise((resolve, reject) => {
    const filename = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
    const storageRef = ref(storage, `properties/${userId}/${filename}`);
    const task = uploadBytesResumable(storageRef, file);

    task.on(
      "state_changed",
      (snapshot) => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress?.(pct);
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve(url);
      }
    );
  });
}

/**
 * Upload multiple images and return array of URLs.
 */
export async function uploadMultipleImages(files, userId, onProgress) {
  const urls = [];
  for (let i = 0; i < files.length; i++) {
    const url = await uploadPropertyImage(files[i], userId, (pct) => {
      onProgress?.({ index: i, total: files.length, pct });
    });
    urls.push(url);
  }
  return urls;
}

// ════════════════════════════════════════════════════
// USER PROFILE
// ════════════════════════════════════════════════════

export async function getUserProfile(userId) {
  const snap = await getDoc(doc(db, USERS, userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateUserProfile(userId, data) {
  await updateDoc(doc(db, USERS, userId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

// ════════════════════════════════════════════════════
// ENQUIRIES
// ════════════════════════════════════════════════════

export async function sendEnquiry(propertyId, fromUserId, toUserId, message) {
  await addDoc(collection(db, ENQUIRIES), {
    propertyId,
    fromUserId,
    toUserId,
    message,
    read: false,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, PROPERTIES, propertyId), { enquiries: increment(1) });
}

export async function getMyEnquiries(userId) {
  const q = query(
    collection(db, ENQUIRIES),
    where("toUserId", "==", userId),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ════════════════════════════════════════════════════
// ADMIN
// ════════════════════════════════════════════════════

export async function getPendingProperties() {
  const q = query(
    collection(db, PROPERTIES),
    where("status", "==", "pending"),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function approveProperty(id) {
  await updateDoc(doc(db, PROPERTIES, id), {
    status: "approved",
    verified: true,
    updatedAt: serverTimestamp(),
  });
}

export async function rejectProperty(id) {
  await updateDoc(doc(db, PROPERTIES, id), {
    status: "rejected",
    updatedAt: serverTimestamp(),
  });
}
